import { Location } from '@cobliteam/common-types';
import DeviceStatusCell from '@cobliteam/devices/src/containers/DataTableDevices/components/DeviceStatusCell';
import { useTranslation } from '@cobliteam/i18n';
import Icon from '@cobliteam/ui/icons';
import { date } from '@cobliteam/ui/utils';
import { useFleetCategories } from '../../../globalSidebarDef';
import { deviceTypeLabel, getCategory, vehicleLabel } from '../../../helpers';
import { useSearch } from '../../../hooks/useSearch';
import { type DeviceWithCategory, EnumCategory } from '../../../types';
import CardHeader from '../../Cards/CardHeader';
import CardItem, { type CardItemProps } from '../../Cards/CardItem';
import CardMap from '../../Cards/CardMap';

const DevicesDetails = () => {
  const { t } = useTranslation('global-search');
  const { data, selectedItem } = useSearch();
  const item = selectedItem as DeviceWithCategory;
  const fleetCategories = useFleetCategories(t);

  if (!item) return null;

  const vehicle = item?.vehicle;

  const vehicleCategory = getCategory(EnumCategory.VEHICLES, fleetCategories);
  const driverCategory = getCategory(EnumCategory.DRIVERS, fleetCategories);
  const groupsCategory = getCategory(EnumCategory.VEHICLE_GROUPS, fleetCategories);

  if (!(vehicleCategory && driverCategory && groupsCategory)) return null;

  const cardVehicle: CardItemProps = {
    disabled: !vehicle,
    title: vehicle ? vehicle?.licensePlate || '---' : t('device_details.no_vehicle_associated'),
    subtitle: vehicleLabel(vehicle),
    tooltip: vehicle ? t('device_details.associated_vehicle') : t('device_details.no_vehicle_associated'),
    icon: vehicleCategory.icon,
    color: vehicleCategory.color,
    itemLink: `${Location.VEHICLES}?search=${vehicle?.licensePlate}`,
    pageLink: `${Location.DEVICES}?search=${item.cobliId}`,
  };

  const cardDriver: CardItemProps = {
    disabled: !item.vehicle?.driver?.id,
    title: item.vehicle?.driver?.name || t('driver_details.no_driver_associated'),
    tooltip: item.vehicle?.driver?.id ? t('driver_details.associated_driver') : t('driver_details.no_driver_associated'),
    subtitle: `Nº Telefone: ${item.vehicle?.driver?.phoneNumbers?.map((phone) => phone).join(', ') || '---'}`,
    icon: driverCategory.icon,
    color: driverCategory.color,
    itemLink: `${Location.DRIVER_MANAGEMENT}?search=${item.vehicle?.driver?.id}`,
    pageLink: undefined,
  };

  const cardGroups: CardItemProps = {
    disabled: !item.vehicle?.groups?.length,
    title: item.vehicle?.groups[0]?.name || t('vehicle_group_details.no_group_associated'),
    tooltip: item.vehicle?.groups?.length ? t('device_details.associated_group') : t('device_details.no_group_associated'),
    icon: groupsCategory.icon,
    color: groupsCategory.color,
    itemLink: `${Location.VEHICLES_GROUPS}?search=${item.vehicle?.groups[0]?.id}`,
    pageLink: undefined,
  };

  const title = `${item.cobliId} `;
  const subtitle = deviceTypeLabel(item?.generalType);
  const tab = vehicle ? 'Associado' : 'Desassociado';

  return (
    <div className='relative flex h-[500px] w-full flex-col gap-4 overflow-hidden overflow-y-auto p-4'>
      <CardHeader
        icon={item.category.icon}
        color={item.category.color}
        title={title}
        subtitle={subtitle}
        link={`${Location.DEVICES}?search=${item.cobliId}&tab=${tab}`}
        linkTooltip={t('device_details.go_to_devices_page')}
      />

      {/*Details*/}
      <div className='grid grid-cols-12 gap-4'>
        <div className='col-span-12'>
          <div className='mb-1 flex items-center gap-2 font-medium text-xs'>
            <Icon name='info' className='!size-3' />
            {t('device_details.device_status')}
          </div>
          <div className='ml-5 w-full'>
            <DeviceStatusCell device={item} size='sm' />
          </div>
        </div>
        <div className='col-span-12'>
          <div className='mb-1 flex items-center gap-2 font-medium text-xs'>
            <Icon name='info' className='!size-3' />
            {t('device_details.information')}
          </div>
          <div className='ml-5 w-full'>
            <div className='text-xs'>
              {t('device_details.last_update')} {item?.lastLocation?.cobliIngestedAt ? date.format(item?.lastLocation?.cobliIngestedAt, 'dd/MM/yyyy - HH:mm:ss') : '---'}
            </div>
          </div>
        </div>
      </div>

      {/*Cards*/}
      <div className=' space-y-2'>
        <CardItem {...cardVehicle} />
        <CardItem {...cardDriver} />
        <CardItem {...cardGroups} />
      </div>

      {/*Map*/}
      <div>
        <CardMap
          title='Última localização do dispositivo'
          latitude={item?.lastLocation?.latitude}
          longitude={item?.lastLocation?.longitude}
          updatedAt={item?.lastLocation?.cobliIngestedAt}
          linkTo={`/monitoring?search=${item?.vehicle?.licensePlate}&focus=true`}
        />
      </div>
    </div>
  );
};

export default DevicesDetails;
